import { useNavigate } from 'react-router-dom';
import { 
  User, 
  BarChart3, 
  SlidersHorizontal, 
  LogOut,
  Globe,
  Languages
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { useAvatar } from '@/contexts/AvatarContext';
import { ROUTES } from '@/constants';
import { LCommon } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY } from '@/design-system';

interface AccountSheetContentProps {
  onClose: () => void;
}

interface SheetNavItem {
  icon: typeof User;
  labelKey: string;
  labelDefault: string;
  path: string;
}

const navItems: SheetNavItem[] = [
  { icon: User, labelKey: 'sidebar.profile', labelDefault: 'Profile', path: ROUTES.ACCOUNT_PROFILE },
  { icon: BarChart3, labelKey: 'sidebar.performance', labelDefault: 'Performance', path: ROUTES.ACCOUNT_PERFORMANCE },
  { icon: SlidersHorizontal, labelKey: 'sidebar.local_settings', labelDefault: 'Preferences', path: ROUTES.ACCOUNT_SETTINGS },
];

/**
 * Content for the account slide-up sheet on mobile
 */
export function AccountSheetContent({ onClose }: AccountSheetContentProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { settings } = useLocale();
  const { avatarUrl, displayName } = useAvatar();
  const { lCommon } = useLocalizations();
  
  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };
  
  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate(ROUTES.ROOT);
  };
  
  // Get user initials for avatar fallback
  const userInitials = user?.email?.substring(0, 2).toUpperCase() || 'U';
  
  return (
    <div className="py-4 space-y-4">
      {/* User Info Header */}
      <div className="flex items-center gap-3 px-2">
        <Avatar className="h-12 w-12">
          <AvatarImage src={avatarUrl} alt={lCommon('aria.user_avatar', { defaultValue: 'User avatar' })} />
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            {userInitials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className={cn(TYPOGRAPHY.cardTitle, 'truncate')}>
            {displayName || user?.email?.split('@')[0] || 'User'}
          </p>
          <p className={cn(TYPOGRAPHY.labelMuted, 'truncate')}>
            {user?.email}
          </p>
        </div>
      </div>
      
      <Separator />
      
      {/* Navigation Items */}
      <div className="space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <Button
              key={item.path}
              variant="ghost"
              className="w-full justify-start gap-3 h-12 px-4"
              onClick={() => handleNavigate(item.path)}
            >
              <Icon className="h-5 w-5 text-muted-foreground" />
              <span className="font-medium">
                <LCommon id={item.labelKey} defaultValue={item.labelDefault} />
              </span>
            </Button>
          );
        })}
      </div>
      
      <Separator />
      
      {/* Location/Language (subtle) */}
      <div className="px-4 py-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Globe className="h-4 w-4" />
          <span>{settings.displayName || 'International'}</span>
          <span className="mx-1">•</span>
          <Languages className="h-4 w-4" />
          <span>{settings.language?.toUpperCase() || 'EN'}</span>
        </div>
      </div>
      
      <Separator />
      
      {/* Sign Out */}
      <div className="px-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleSignOut}
        >
          <LogOut className="h-5 w-5" />
          <span className="font-medium">
            <LCommon id="actions.sign_out" defaultValue="Sign Out" />
          </span>
        </Button>
      </div>
    </div>
  );
}
